import { Fragment, type ReactNode } from 'react';
import { Heading } from '../atoms/Typography/Heading';
import { Text } from '../atoms/Typography/Text';
import { Stack } from '../atoms/Stack/Stack';
import { parseReleaseMarkdown } from '../../services/releaseNotesService';
import './ReleaseNotePreview.css';

/** "**bold**" → <strong>; everything else stays text (React escapes it). */
function inline(text: string): ReactNode {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) =>
    part.startsWith('**') && part.endsWith('**') && part.length > 4 ? (
      <strong key={i}>{part.slice(2, -2)}</strong>
    ) : (
      <Fragment key={i}>{part}</Fragment>
    ),
  );
}

export interface ReleaseNotePreviewProps {
  title: string;
  body: string;
  /** Heading level of the note's own title (its group headings are one level below). */
  titleLevel?: 3 | 4;
  lang: 'de' | 'en';
}

/**
 * Renders a release note as the e-mail shows it (the Markdown subset of the
 * backend renderer: headings, bullet lists, paragraphs, **bold**). No HTML is
 * injected — every value is a React text node.
 */
export function ReleaseNotePreview({ title, body, titleLevel = 3, lang }: ReleaseNotePreviewProps) {
  const groupLevel = titleLevel === 3 ? 4 : 5;
  return (
    <Stack gap="3" lang={lang} className="release-note-preview">
      <Heading level={titleLevel}>{title}</Heading>
      {parseReleaseMarkdown(body).map((block, i) => {
        if (block.type === 'heading') {
          return (
            <Heading key={i} level={groupLevel}>
              {inline(block.text)}
            </Heading>
          );
        }
        if (block.type === 'list') {
          return (
            <ul key={i} className="release-note-preview__list">
              {block.items.map((item, j) => (
                <li key={j}>{inline(item)}</li>
              ))}
            </ul>
          );
        }
        return <Text key={i}>{inline(block.text)}</Text>;
      })}
    </Stack>
  );
}

export default ReleaseNotePreview;
